import type { GdalFieldInfo, GdalLayerInfo } from '@app/modules/Home/Data/types'
import gdalWasmUrl from 'gdal3.js/dist/package/gdal3WebAssembly.wasm?url'
import gdalDataUrl from 'gdal3.js/dist/package/gdal3WebAssembly.data?url'

type OgrInfoField = {
  name: string
  type: string
  [key: string]: unknown
}

type OgrInfoGeometryField = {
  name: string
  type: string
  coordinateSystem?: { wkt?: string }
}

type OgrInfoLayer = {
  name: string
  featureCount: number
  geometryFields?: OgrInfoGeometryField[]
  fields?: OgrInfoField[]
}

type OgrInfoResult = {
  layers?: OgrInfoLayer[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let gdalSingleton: Promise<any> | null = null

// useWorker: false — the default worker path resolves to a relative URL ('gdal3.js')
// that 404s in Vite, causing initGdalJs to return a promise that hangs forever.
function getGdal() {
  if (!gdalSingleton) {
    gdalSingleton = import('gdal3.js').then(({ default: initGdalJs }) =>
      initGdalJs({
        useWorker: false,
        paths: { wasm: gdalWasmUrl, data: gdalDataUrl },
      }),
    )
  }
  return gdalSingleton
}

export async function extractGdalInfo(files: File[]): Promise<GdalLayerInfo> {
  const gdal = await getGdal()

  // gdal3.js types declare FileList but the runtime handles File[] identically
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { datasets, errors } = await gdal.open(files as any)
  if (!datasets.length) {
    throw new Error(errors.length ? errors.join('; ') : 'Could not open file with GDAL')
  }

  const dataset = datasets[0]

  // dataset.info comes from an internal ogrinfo call without -al, so it never
  // includes the fields array. Call ogrinfo explicitly with -al -so to get both
  // field definitions and geometry metadata in one pass.
  let result: OgrInfoResult
  try {
    result = (await gdal.ogrinfo(dataset, ['-al', '-so'])) as OgrInfoResult
  } finally {
    await gdal.close(dataset)
  }

  const layers = result?.layers ?? []
  if (!layers.length) {
    throw new Error('No vector layers found in file')
  }

  const layer = layers[0]
  const geometryField = layer.geometryFields?.[0]

  const fields: GdalFieldInfo[] = (layer.fields ?? []).map((f) => ({
    name: f.name,
    type: f.type,
  }))

  return {
    layerName: layer.name,
    geometryType: geometryField?.type ?? 'Unknown',
    featureCount: layer.featureCount,
    fields,
    crs: geometryField?.coordinateSystem?.wkt,
    skippedLayerNames: layers.length > 1 ? layers.slice(1).map((l) => l.name) : undefined,
  }
}
